import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { setHomepageModules, toggleHomepageModule, updateCustomPage, updateEventNavbar, updateHomepageFooter, updateHomepageHeader, updateHomepageModule, updatePostRegisterHomeModules } from '../../../../../../store/actions/admin/create-event';
import { CustomPage, EventNavbar, HomepageFooter, HomepageHeader, IPostRegisterHomeModule, LanguagesAbbr, ModuleMap, NavMainItems, PageModule, PageModuleType } from '../../../../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import EditLayoutModal from '../../../homepage/navigation/structure/edit-module-layout-modal/edit-module-layout-modal';
import EditFooterModal from './edit-footer-modal';
import EditHeaderModal from './edit-header-modal';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import './controls.scss';
import { ParamsProps } from '../../../../../live-event/live-event';
import { useParams } from 'react-router';
import { UpdateCustomPage } from '../../../../../../connection/custom-page';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { UpdatePostRegisterPreviewModules } from '../../../../../../connection/events';
import { getCurrentCustomPage } from '../../../../../../utils/admin-routing-utils';
import { hasFooterControls, hasHeaderControls, hasNavbarControls, hasPageModuleControls } from '../../../../../../utils/controls-utils';
import { useIsNewNavigation } from '../../../../../../hooks/navigation.hooks';
import { EditorSizes } from '../../../../../../types/template-layouts';

export interface InitialControlsDisplay {
	editColors?: boolean;
	editLayout?: boolean;
	addContent?: boolean;
	changeSectionPosition?: boolean;
	removeSection?: boolean;
	reorderContent?: boolean;
	contentManagement?: boolean;
}

export const InitialControlsDisplay = {
	editColors: true,
	editLayout: true,
	addContent: true,
	changeSectionPosition: true,
	removeSection: false,
	reorderContent: false,
	contentManagement: false
};

export interface IEditableContent {
	jsx: JSX.Element;
	onDone: () => void;
}

type ModuleTypes = PageModule | HomepageFooter | HomepageHeader | EventNavbar;

interface Props {
	openContentModal?: () => void;
	openEditModal?: () => void;
	openQuizEditModal?: () => void;
	openReorderModal?: () => void;
	openContentManagementModal?: () => void;
	module: ModuleTypes;
	contentButtonText?: string;
	editableContent?: IEditableContent;
	display?: InitialControlsDisplay;
	addContentIconAsEditIcon?: boolean //makes the add content button use the edit icon
	baseLanguage: LanguagesAbbr;
	language: LanguagesAbbr;
	interceptFinished?: (
		module: ModuleTypes,
		backgroundOverrides: any,
		setOpenStyleModal: React.Dispatch<React.SetStateAction<boolean>>
	) => void;
	disabled?: boolean;
}

const Controls: React.FC<Props> = (props: Props) => {
	const {
		module,
		openContentModal,
		openQuizEditModal,
		openReorderModal,
		openContentManagementModal,
		openEditModal,
		contentButtonText,
		editableContent,
		display = InitialControlsDisplay,
		addContentIconAsEditIcon = false,
		baseLanguage,
		language,
		interceptFinished,
		disabled,
	} = props;
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const { workingEvent } = useTypedSelector((state) => state.CreateEventReducer);
	const { currentPostRegisterPage } = useTypedSelector(state => state.CreateEventReducer);
	const token = useTypedSelector(state => state.AuthReducer.token);

	const dispatch = useDispatch();
	const { customPath } = useParams() as ParamsProps;

	const [openLayoutModal, setOpenLayoutModal] = useState<PageModuleType | null>(null);
	const [openStyleModal, setOpenStyleModal] = useState(false);
	const [workingPageModule, setWorkingPageModule] = useState<PageModule | null>();
	const [openFooterModal, setOpenFooterModal] = useState<boolean>(false);
	const [openHeaderModal, setOpenHeaderModal] = useState<boolean>(false);
	const [selectedModuleId, setSelectedModuleId] = useState<PageModuleType | null>(null);
	const [title, setTitle] = useState('');
	const isNewNavigation = useIsNewNavigation();

	const currentCustomPage = useMemo(
		() => getCurrentCustomPage(workingEvent?.custom_pages, customPath),
		[workingEvent?.custom_pages, customPath]
	);

	useEffect(() => {
		if (hasPageModuleControls(module)) setWorkingPageModule(module);
	}, [module]);
	
	const isDesktop = editorSize === EditorSizes.desktop;

	function makeBackgroundStylingOverrides(colorKey: string) {
		const isColor = !colorKey.includes('.');
		const isRestoringDefault = colorKey === "";

		//If we are restoreing default we want it to just be a blank string, if changing color we want the classname with appended"-background"
		if (isColor && !isRestoringDefault) {
			colorKey += "-background";
		}
		return {
			color: (module as PageModule)?.styling_overrides?.background?.color ?? '',
			image: { is_on: false, src: (module as PageModule)?.styling_overrides?.background?.image.src ?? '' },
			[isColor ? 'color' : 'image']: isColor ? colorKey : { is_on: true, src: colorKey }
		};
	}

	function updateModuleBackground(dispatchFunc: any, background: any) {
		dispatch(dispatchFunc({
			...module,
			styling_overrides: {
				...module.styling_overrides,
				background
			}
		}));
	}

	function handleFinishStyling(colorKey: string) {
		const background = makeBackgroundStylingOverrides(colorKey);

		if (interceptFinished) {
			return interceptFinished(
				module,
				background,
				setOpenStyleModal,
			);
		}

		if (hasPageModuleControls(module)) {
			updateModuleBackground(updateHomepageModule, background);
		}
		if (hasHeaderControls(module)) {
			updateModuleBackground(updateHomepageHeader, background);
		}
		if (hasFooterControls(module)) {
			updateModuleBackground(updateHomepageFooter, background);
		}
		if (hasNavbarControls(module)) {
			updateModuleBackground(updateEventNavbar, background);
		}
		setOpenStyleModal(false);
	}

	const handleCancelStyling = useCallback(() => {
		setOpenStyleModal(false);
	}, []);


	const handleOpenEditModal = () => {
		const pageModule: PageModule = module as PageModule;
		
		if (isNewNavigation && openEditModal && display.editLayout) {
			openEditModal();
		}
		
		if (openQuizEditModal) {
			openQuizEditModal();
		} else if (openEditModal && pageModule?.content?.layout_type === "calendar") {
			openEditModal();
		} else {
			openEditModalDefault();
		}
	};

	function openEditModalDefault() {

		if (hasPageModuleControls(module)) {
			setSelectedModuleId(module?.id || 0);
			switch (module.type) {
				case PageModuleType.main_event_banner:
					setOpenLayoutModal(PageModuleType.main_event_banner);
					break;
				case PageModuleType.speakers:
					setOpenLayoutModal(PageModuleType.speakers);
					break;
				case PageModuleType.about:
					setOpenLayoutModal(PageModuleType.about);
					break;
				case PageModuleType.agenda:
					setOpenLayoutModal(PageModuleType.agenda);
					break;
				case PageModuleType.products:
					setOpenLayoutModal(PageModuleType.products);
					break;
				case PageModuleType.sponsors:
					setOpenLayoutModal(PageModuleType.sponsors);
					break;
				case PageModuleType.videos:
					setOpenLayoutModal(PageModuleType.videos);
					break;
				case PageModuleType.callout:
					setOpenLayoutModal(PageModuleType.callout);
					break;
				case PageModuleType.blank:
					setOpenLayoutModal(PageModuleType.blank);
					break;
				default:
					setOpenLayoutModal(PageModuleType.main_event_banner);
			}
			setTitle(ModuleMap[module.type]);
		}

		// check for header first because footers only property is logo and header modules have logos as well
		if (hasHeaderControls(module)) {
			setTitle('Header');
			setOpenHeaderModal(true);
			return;
		}
		if (hasFooterControls(module)) {
			setTitle('Footer');
			setOpenFooterModal(true);
		}
		if (hasNavbarControls(module)) {
			setTitle('Sidebar');
			setOpenFooterModal(true);
		}
	}

	async function changeModuleIndex(i: number, moveModule: PageModule) {
		const isCustomPage = !!customPath && !!currentCustomPage?.modules;
		const isPostRegHome = currentPostRegisterPage === NavMainItems.Home;

		let _modules;
		if (isCustomPage && currentCustomPage?.modules) {
			_modules = currentCustomPage.modules.slice();
		} else if (isPostRegHome && workingEvent?.homepage) {
			_modules = workingEvent.homepage.post_register_home_modules.slice();
		} else if (workingEvent?.homepage) {
			_modules = workingEvent.homepage.modules.slice(); // use landing page modules
		}

		if (!_modules) return;
		const startIndex = _modules.findIndex((_module: PageModule) => Number(_module.id) === Number(moveModule.id));
		// i will either be 1 or -1, +- = -
		// -1 brings it up in the array
		// 1 pushes it down in the array
		const newIndex = startIndex + i;

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		if (isCustomPage && currentCustomPage) {
			if (!token) return;
			const updatedPage: CustomPage = {
				...currentCustomPage,
				modules: _modules
			};
			dispatch(updateCustomPage(updatedPage)); //update page in redux
			try {
				await UpdateCustomPage(updatedPage, token); //update individual page in db
			} catch (e: any) {
				console.error(e);
			}
		} else if (isPostRegHome) {
			const homepage = workingEvent?.homepage?.homepage;
			if (!token || !homepage) return;
			// update in redux
			const updatedPostRegisterModules = _modules.reduce((accum: IPostRegisterHomeModule[], module: PageModule) => {
				if (!module.id) return accum;
				return [
					...accum,
					{ is_on: module.is_on, module_id: module.id }
				];
			}, []);
			dispatch(updatePostRegisterHomeModules(_modules));
			// update in db
			try {
				await UpdatePostRegisterPreviewModules(homepage, token, updatedPostRegisterModules);
			} catch (e) {
				console.error(e);
			}
		} else {
			dispatch(setHomepageModules(_modules));
		}
	}

	return (
		<div className="section-controls-wrapper">
			<div className="button-container">
				{
					display.editColors &&
					<button
						disabled={disabled}
						className="round"
						onClick={() => setOpenStyleModal(true)}
					>
						<Icon name={ICONS.PAINTBRUSH} size={16} color={COLORS.WHITE} />
					</button>
				}
				{
					display.editLayout && isDesktop &&
					<button
						disabled={disabled}
						className="round"
						onClick={handleOpenEditModal}
					>
						<Icon name={ICONS.EDIT} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
					</button>
				}
				{
					(display.reorderContent && (module as PageModule)?.content?.layout_type === "calendar") &&
					<button
						disabled={disabled}
						className="round"
						onClick={openReorderModal}
					>
						<Icon name={ICONS.REORDER} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
							
					</button>
				}
				{
					display.contentManagement &&
					<button
						disabled={disabled}
						className="round"
						onClick={openContentManagementModal}
					>
						<Icon name={ICONS.SETTINGS_SLIDERS} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
					</button>
				}
				{
					display.addContent &&
					<button
						disabled={disabled}
						className="add-content small"
						// openModal changes to openContentModuleModal, same functionality tho
						onClick={openContentModal}
					>
						<Icon name={addContentIconAsEditIcon ? ICONS.EDIT : ICONS.ADD} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
						<span>{contentButtonText}</span>
					</button>
				}
				{
					display.changeSectionPosition && hasPageModuleControls(module) &&
					<>
						<button
							disabled={disabled}
							className="round"
							onClick={() => changeModuleIndex(-1, module)}
						>
							<Icon name={ICONS.ARROW_UP_THIN} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
						</button>
						<button
							disabled={disabled}
							className="round arrow-down"
							onClick={() => changeModuleIndex(1, module)}
						>
							<Icon name={ICONS.ARROW_UP_THIN} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
						</button>
					</>
				}
				{
					display.removeSection && hasPageModuleControls(module) &&
					<button
						disabled={disabled}
						className="round"
						onClick={() => dispatch(toggleHomepageModule(module))}
					>
						<Icon name={ICONS.DELETE} size={16} color={COLORS.WHITE} backgroundColor={COLORS.DARK_GREY} />
					</button>
				}
			</div>

			{hasPageModuleControls(module) &&
				<EditLayoutModal
					open={!!openLayoutModal || openLayoutModal === 0}
					setOpenLayoutModal={setOpenLayoutModal}
					pageModuleType={openLayoutModal}
					selectedModule={selectedModuleId}
					title={title}
					editModule={editableContent}
				/>
			}
			{hasFooterControls(module) &&
				<EditFooterModal
					open={openFooterModal}
					closeModal={() => setOpenFooterModal(false)}
					baseLanguage={baseLanguage}
					language={language}
				/>
			}
			{hasHeaderControls(module) &&
				<EditHeaderModal
					open={openHeaderModal}
					closeModal={() => setOpenHeaderModal(false)}
					baseLanguage={baseLanguage}
					language={language}
				/>
			}

			<EditSectionColorsModal
				open={openStyleModal}
				title={EContentColorOverrideNames.background}
				backgroundImage={workingPageModule?.styling_overrides?.background?.image.src}
				onFinish={handleFinishStyling}
				onClose={handleCancelStyling}
			/>

		</div>
	);
};

export default Controls;